'use client'

import { forwardRef, useCallback, useImperativeHandle } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CoinsIcon, PlusCircleIcon, SaveIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { CreateCurrencyParams, CurrencyType, UpdateCurrencyParams } from '@/types/currency'
import { setValidationErrors } from '@/lib/utils'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { UnsavedChangesGuardDialog } from '@/components/partials'
import { FormModeEnum } from '@/enums'

type PropsType = {
  initialData?: CurrencyType | null
  onSubmit: (data: CreateCurrencyParams | UpdateCurrencyParams) => void
  onSubmitAndNew?: (data: CreateCurrencyParams | UpdateCurrencyParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type CurrencyFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

const CurrencyForm = forwardRef<CurrencyFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const currencySchema = useCallback(
      () =>
        z.object({
          code: z
            .string()
            .min(1, t('not_blank'))
            .max(10, t('max_value', { value: 10 })),
          name: z
            .string()
            .min(1, t('not_blank'))
            .max(100, t('max_value', { value: 100 })),
          symbol: z
            .string()
            .min(1, t('not_blank'))
            .max(10, t('max_value', { value: 10 })),
          active: z.boolean()
        }),
      [t]
    )

    type FormData = z.infer<ReturnType<typeof currencySchema>>

    const form = useForm<FormData>({
      resolver: zodResolver(currencySchema()),
      defaultValues: {
        code: initialData?.code || '',
        name: initialData?.name || '',
        symbol: initialData?.symbol || '',
        active: initialData?.active !== undefined ? initialData.active : true
      }
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    const handleSubmit = (data: FormData) => {
      onSubmit({
        code: data.code,
        name: data.name,
        symbol: data.symbol,
        active: data.active
      })
    }

    const handleSubmitAndNew = (data: FormData) => {
      onSubmitAndNew?.({
        code: data.code,
        name: data.name,
        symbol: data.symbol,
        active: data.active
      })
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CoinsIcon className="h-5 w-5" />
            {mode === FormModeEnum.CREATE ? t('create_currency') : t('edit_currency')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(
                isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
                scrollToFirstError
              )}
              className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('code')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="USD"
                          disabled={isLoading}
                          maxLength={10}
                          className="uppercase"
                          {...field}
                          onChange={e => field.onChange(e.target.value.toUpperCase())}
                          autoFocus
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('name')}</FormLabel>
                      <FormControl>
                        <Input placeholder="US Dollar" disabled={isLoading} maxLength={100} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="symbol"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('symbol')}</FormLabel>
                      <FormControl>
                        <Input placeholder="$" disabled={isLoading} maxLength={10} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-col items-start justify-start">
                      <div className="space-y-0.5">
                        <FormLabel>{t('active')}</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isLoading}
                          className="mt-2"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                  <XIcon />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading}>
                      <SaveIcon />
                      <span className="hidden sm:inline">{isLoading ? t('saving') : t('save')}</span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading}>
                    {isCreateModeWithSubmitAndNew ? <PlusCircleIcon /> : <SaveIcon />}
                    <span className="hidden sm:inline">
                      {isLoading ? t('saving') : isCreateModeWithSubmitAndNew ? t('save_and_add_new') : t(mode)}
                    </span>
                  </Button>
                </div>
              </div>
              <UnsavedChangesGuardDialog
                open={isNavigationBlocked}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    )
  }
)

CurrencyForm.displayName = 'CurrencyForm'

export default CurrencyForm
